import * as React from 'react'
import clsx from 'clsx'
import { ButtonBase, ButtonBaseProps } from '../ButtonBase'
import { SvgIcon } from '../SvgIcon'
import { useSlotProps } from '../utils/useSlotProps'
import { SlotComponentProps } from '../utils/types'
import ChevronLeft from '../internal/svg-icons/ChevronLeft'
import ChevronRight from '../internal/svg-icons/ChevronRight'
import style from './index.module.scss'

export interface TabScrollButtonClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
}
/* eslint-disable-next-line */
export interface TabScrollButtonStartIconSlotPropsOverrides {}
/* eslint-disable-next-line */
export interface TabScrollButtonEndIconSlotPropsOverrides {}

export interface TabScrollButtonOwnerState extends TabScrollButtonProps {
  isRtl: boolean
}

export interface TabScrollButtonProps extends ButtonBaseProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<TabScrollButtonClasses>
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: {
    StartScrollButtonIcon?: React.ElementType
    EndScrollButtonIcon?: React.ElementType
  }
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   * @default {}
   */
  slotProps?: {
    startScrollButtonIcon?: SlotComponentProps<
      typeof SvgIcon,
      TabScrollButtonStartIconSlotPropsOverrides,
      TabScrollButtonOwnerState
    >
    endScrollButtonIcon?: SlotComponentProps<
      typeof SvgIcon,
      TabScrollButtonEndIconSlotPropsOverrides,
      TabScrollButtonOwnerState
    >
  }
  /**
   * The direction the button should indicate.
   */
  direction: 'left' | 'right'
  /**
   * The component orientation (layout flow direction).
   */
  orientation: 'horizontal' | 'vertical'
}

export const TabScrollButton = React.forwardRef<
  HTMLButtonElement,
  TabScrollButtonProps
>(function TabScrollButton(props, ref) {
  const {
    slots = {},
    slotProps = {},
    direction,
    orientation,
    disabled,
    classes,
    className,
    ...other
  } = props

  const isRtl = false
  const ownerState = { isRtl, ...props }

  const StartButtonIcon = slots.StartScrollButtonIcon ?? ChevronLeft
  const EndButtonIcon = slots.EndScrollButtonIcon ?? ChevronRight

  const startButtonIconProps = useSlotProps({
    elementType: StartButtonIcon,
    externalSlotProps: slotProps.startScrollButtonIcon,
    additionalProps: {
      size: 'small',
    },
    ownerState,
  })

  const endButtonIconProps = useSlotProps({
    elementType: EndButtonIcon,
    externalSlotProps: slotProps.endScrollButtonIcon,
    additionalProps: {
      size: 'small',
    },
    ownerState,
  })

  return (
    <ButtonBase
      Component="div"
      classes={{
        ...classes,
        root: clsx(style.root, classes?.root),
        disabled: clsx(style.disabled, classes?.disabled),
      }}
      className={clsx(
        orientation === 'vertical' && [
          style['orientation-vertical'],
          classes?.vertical,
        ],
        isRtl && style['isRtl'],
        className
      )}
      role={undefined}
      // @ts-ignore
      tabIndex={null}
      ref={ref}
      disabled={disabled}
      {...other}
    >
      {direction === 'left' ? (
        <StartButtonIcon {...startButtonIconProps} />
      ) : (
        <EndButtonIcon {...endButtonIconProps} />
      )}
    </ButtonBase>
  )
})
